import { NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  linkedSignal,
  OnInit,
  output,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import {
  AccountType,
  DspAdvertiserDealDealTypeEnum,
  DspAdvertiserDealSourceEnum,
  DspCampaign,
  DspCreative,
  Marketplace,
  Strategy,
  StrategyDspFunnel,
} from "@front/m19-api-client";
import {
  AccountGroup,
  BrandingFilter,
  CatalogBrandEx,
  DspFunnelDescriptions,
  Marketplaces,
  ProductGroupEx,
  SegmentEx,
} from "@front/m19-models";
import {
  IButtonDirective,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuSeparatorComponent,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  ISelectMenuImports,
  ISwitchButtonComponent,
  ITooltipDirective,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { AccountBidderStatus } from "@m19-board/services/account-settings.service";
import { AccountBidderStatusFormatterComponent } from "./filter-formatters/account-bidder-status-formatter.component";
import { AccountTypeFormatterComponent } from "./filter-formatters/account-type-formatter.component";
import { AdTypeFormatterComponent } from "./filter-formatters/ad-type-formatter.component";
import { MarketplaceFormatterComponent } from "./filter-formatters/marketplace-formatter.component";
import { StrategyFormatterComponent } from "./filter-formatters/strategy-formatter.component";
import { StringFormatterComponent } from "./filter-formatters/string-formatter.component";

export enum AdType {
  SponsoredAds,
  DSP,
}

export enum FilterCategory {
  Marketplace = "Marketplace",
  AccountGroup = "AccountGroup",
  AccountType = "AccountType",
  AccountBidderStatus = "AccountBidderStatus",
  KeywordSegment = "KeywordSegment",
  ProductSegment = "ProductSegment",
  ProductGroup = "ProductGroup",
  BrandTraffic = "BrandTraffic",
  Strategy = "Strategy",
  BrandProducts = "BrandProducts",
  Brand = "Brand",
  ProductTag1 = "ProductTag1",
  ProductTag2 = "ProductTag2",
  DspCampaign = "DspCampaign",
  DspCreative = "DspCreative",
  Bulk = "Bulk",
  AdType = "AdType",
  DspFunnel = "DspFunnel",
  DspDealSource = "DspDealSource",
  DspDealType = "DspDealType",
}

export type FilterCategoryType = {
  [FilterCategory.Marketplace]: Marketplace;
  [FilterCategory.AccountGroup]: AccountGroup;
  [FilterCategory.AccountType]: AccountType;
  [FilterCategory.AccountBidderStatus]: AccountBidderStatus;
  [FilterCategory.ProductGroup]: ProductGroupEx;
  [FilterCategory.KeywordSegment]: SegmentEx;
  [FilterCategory.ProductSegment]: SegmentEx;
  [FilterCategory.BrandTraffic]: BrandingFilter;
  [FilterCategory.Strategy]: Strategy;
  [FilterCategory.BrandProducts]: BrandingFilter;
  [FilterCategory.Brand]: CatalogBrandEx;
  [FilterCategory.ProductTag1]: string;
  [FilterCategory.ProductTag2]: string;
  [FilterCategory.DspCampaign]: DspCampaign;
  [FilterCategory.DspCreative]: DspCreative;
  [FilterCategory.Bulk]: string;
  [FilterCategory.AdType]: AdType;
  [FilterCategory.DspFunnel]: StrategyDspFunnel;
  [FilterCategory.DspDealSource]: DspAdvertiserDealSourceEnum;
  [FilterCategory.DspDealType]: DspAdvertiserDealDealTypeEnum;
};

export interface Filter<T extends FilterCategory> {
  id: string;
  type: T;
  label: string;
  placeholder: string;
  icon?: string;
  options: () => FilterCategoryType[T][];
  filteredOptions?: FilterCategoryType[T][];
  values: FilterCategoryType[T][];
  unique?: boolean;
  tooltip?: string;
  excludeModeAllowed?: boolean;
  exclude?: boolean;
  beingAdded?: boolean;
  category?: string;
  searchable?: boolean;
  localStorageKey?: string;
  selectAllOption?: boolean;
}

interface CategoryFilterObj<T extends FilterCategory> {
  category: string;
  filters: Filter<T>[];
}

@Component({
  selector: "app-compact-filter",
  imports: [
    ISelectMenuImports,
    IButtonDirective,
    IDropdownMenuComponent,
    IDropdownMenuTriggerDirective,
    IDropdownMenuItemDirective,
    TranslocoDirective,
    NgTemplateOutlet,
    MarketplaceFormatterComponent,
    AccountTypeFormatterComponent,
    ITooltipDirective,
    AccountBidderStatusFormatterComponent,
    StrategyFormatterComponent,
    StringFormatterComponent,
    IDropdownMenuSeparatorComponent,
    ISwitchButtonComponent,
    FormsModule,
    AdTypeFormatterComponent,
    IIconComponent,
  ],
  templateUrl: "./compact-filter.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompactFilterComponent<T extends FilterCategory> implements OnInit {
  readonly Marketplaces = Marketplaces;
  readonly FilterCategory = FilterCategory;
  readonly AccountType = AccountType;
  readonly dspFunnelTranslationKeys = Object.fromEntries(
    Object.entries(DspFunnelDescriptions).map(([funnel, description]) => [funnel, description.title]),
  );

  readonly dspDealSourceTranslationKeys = Object.fromEntries(
    Object.values(DspAdvertiserDealSourceEnum).map((source) => [source, `dsp-media-selector.source_${source}`]),
  );
  readonly dspDealTypeTranslationKeys = Object.fromEntries(
    Object.values(DspAdvertiserDealDealTypeEnum).map((dealType) => [
      dealType,
      `dsp-media-selector.deal_type_${dealType}`,
    ]),
  );

  readonly filters = input.required<Filter<T>[]>();

  private readonly _filters = linkedSignal(() => this.filters());

  filterChange = output<Filter<T>[]>();

  readonly selectedFilters = computed(() =>
    this._filters().filter((f) => ((f.values && f.values.length > 0) || f.beingAdded) ?? false),
  );

  private readonly availableFilters = computed(() => {
    return this._filters().filter((f) => !f.unique || !this.isSelectedFilter(f));
  });

  readonly availableFiltersByCategory = computed<CategoryFilterObj<T>[]>(() => {
    const res: CategoryFilterObj<T>[] = [];
    for (const f of this.availableFilters()) {
      const category = f.category ?? "";
      if (!res.some((c) => c.category === category)) {
        res.push({ category, filters: [] });
      }
      res.find((c) => c.category === category)!.filters.push(f);
    }
    return res;
  });

  queryFilter = signal<string | undefined>("");
  readonly selectedFiltersQuery = computed<Filter<T>[]>(() => {
    const query = this.queryFilter() ?? "";
    const selectedFilters = this.selectedFilters();
    return selectedFilters.map((f) => {
      if (!f.searchable) return { ...f, filteredOptions: f.options() };
      const labelField = this.getLabelField(f);
      const filteredOptions = f.options().filter((o) => this.passesFilterQuery(labelField, query, o));
      return { ...f, filteredOptions };
    });
  });

  private hasChanged = false;

  addFilter(filter: Filter<T>) {
    this._filters.update((filters) => filters.map((f) => (f.type === filter.type ? { ...f, beingAdded: true } : f)));
  }

  onFilterValueChange(filter: Filter<T>, value: FilterCategoryType[T][] | undefined, exclude?: boolean) {
    this.hasChanged = true;
    this._filters.update((filters) =>
      filters.map((f) => (f.type === filter.type ? { ...f, values: value ?? [], exclude: exclude ?? f.exclude } : f)),
    );
  }

  menuClosed(filter: Filter<T>) {
    this.queryFilter.set("");
    if (filter.beingAdded) {
      this._filters.update((filters) => filters.map((f) => (f.type === filter.type ? { ...f, beingAdded: false } : f)));
    }
    if (this.hasChanged) {
      this.emitFilters();
      this.hasChanged = false;
    }
  }

  removeFilter(filter: Filter<T>) {
    this.onFilterValueChange(filter, undefined, false);
    this.emitFilters();
  }

  ngOnInit(): void {
    for (const filter of this._filters()) {
      if (filter.localStorageKey) {
        this._filters.update((filters) =>
          filters.map((f) => (f.type === filter.type ? { ...f, values: this.loadDefaultValues(filter) } : f)),
        );
        this.emitFilters();
      }
    }
  }

  constructor() {
    effect(() => {
      for (const filter of this._filters()) {
        if (filter.localStorageKey) {
          if (filter.values?.length) {
            localStorage.setItem(filter.localStorageKey, JSON.stringify(filter.values));
          } else {
            localStorage.removeItem(filter.localStorageKey);
          }
        }
      }
    });
  }

  private emitFilters() {
    this.filterChange.emit(this._filters().map((f) => ({ ...f, values: f.values ?? [] })));
  }

  private isSelectedFilter(filter: Filter<T>) {
    return this.selectedFilters().some((f) => f.type === filter.type && f.exclude === filter.exclude);
  }

  private getLabelField(filter: Filter<T>): string | undefined {
    switch (filter.type) {
      case FilterCategory.AccountType:
      case FilterCategory.AccountBidderStatus:
      case FilterCategory.Bulk:
      case FilterCategory.ProductTag1:
      case FilterCategory.ProductTag2:
      case FilterCategory.Marketplace:
      case FilterCategory.DspFunnel:
        return undefined;
      case FilterCategory.Brand:
        return "brandName";
      case FilterCategory.ProductGroup:
        return "productGroupName";
      default:
        return "name";
    }
  }

  private passesFilterQuery(labelField: string | undefined, query: string, filterValue: FilterCategoryType[T]) {
    if (!query) return true;
    let value: string | undefined;
    if (labelField) {
      value = filterValue[labelField as keyof FilterCategoryType[T]] as unknown as string;
    } else {
      value = filterValue.toString();
    }
    return value?.toLowerCase().includes(query.toLowerCase()) ?? false;
  }

  private loadDefaultValues(filter: Filter<T>) {
    if (filter.localStorageKey) {
      const stored = localStorage.getItem(filter.localStorageKey);
      try {
        if (stored) return JSON.parse(stored);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Failed to parse stored values - removing it", e);
        localStorage.removeItem(filter.localStorageKey);
        return [];
      }
    }
  }
}
